import { parseCsv } from "./csv";
import { openXlsx } from "./xlsx";

/** A spreadsheet file opened for import: its sheets (a CSV has one) and the cells of any of them as text */
export type Workbook = { sheets: string[]; read: (index: number) => Promise<string[][]> };

export const SPREADSHEET_ACCEPT = ".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/** A number as Excel shows it typed, not 9.87654E+09: phone numbers saved as numbers keep every digit */
const text = (v: unknown): string => (typeof v === "number" ? (Number.isFinite(v) ? String(v) : "") : typeof v === "boolean" ? (v ? "TRUE" : "FALSE") : v == null ? "" : String(v));

/**
 * .csv and .xlsx are read here without a library. Old .xls files need a reader for Excel's binary format; it is loaded
 * only when someone chooses one (SheetJS, kept out of the page's main file).
 */
export async function openWorkbook(file: File): Promise<Workbook> {
  if (/\.csv$/i.test(file.name)) {
    const rows = parseCsv(await file.text());
    return { sheets: ["CSV"], read: async () => rows };
  }
  if (/\.xlsx$/i.test(file.name)) return openXlsx(file);
  if (/\.xls$/i.test(file.name)) {
    const XLSX = await import("xlsx");
    const book = XLSX.read(new Uint8Array(await file.arrayBuffer()), { type: "array", cellDates: false });
    const names = book.SheetNames.filter((n) => !book.Workbook?.Sheets?.find((s) => s.name === n && s.Hidden));
    if (!names.length) throw new Error("The workbook has no sheet to read");
    return {
      sheets: names,
      read: async (i) => {
        const ws = book.Sheets[names[i]!];
        if (!ws) throw new Error("The workbook has no sheet to read");
        return XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: "", blankrows: false }).map((r) => r.map(text));
      },
    };
  }
  throw new Error("Choose a .csv, .xlsx or .xls file");
}
